import * as THREE from "three";
import { CosmeticPicker, type BailStyleId, type Random } from "../settings";
import type { Cue } from "../sound/cues";
import { Fireworks, MAX_SPARKS, type FireworkOrigin } from "./fireworks";
import type { SkaterLook } from "./skater-parts";

// Bail styles (#83): what a bail looks like, on top of (or instead of) the
// model's own tumble. Render-only: the sim's bail, its length and the
// respawn don't change. `classic` is the tumble alone; `explode` knocks the
// head, arms, legs and board off with a pop and a puff of stars, and they
// bounce round until the respawn puts the rider back together; `confetti`
// bursts the rider into confetti in their own colors, with a party horn;
// `fireworks` (#199, #210) turns the rider into the show, like confetti:
// the body vanishes and a rocket goes up from where each part was, to
// burst overhead in that part's color (`fireworks.ts`), drawn as one
// `THREE.Points`.
// Everything drawn is either a clone of the rider's parts (sharing their
// geometry and materials) or made once here, so bails allocate nothing on
// the GPU.

/** What a bail style needs from the skater each frame. */
export interface BailSkater {
  mode: string;
  bailT: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vz: number;
}

/** The rider as a bail style sees it. */
export interface BailRider {
  root: THREE.Object3D;
  /** The parts Explode knocks off (`SkaterModel.limbs`). */
  limbs: THREE.Object3D[];
}

/** Pieces fall slower than the skater (cartoon float), and lose this much of their speed per bounce. */
const PIECE_G = 14;
const BOUNCE = 0.45;
/** How far a piece's centre stays above the ground. */
const PIECE_REST = 0.08;
const CONFETTI = 160;
/** Confetti flutters: slow fall and heavy air drag. */
const CONFETTI_G = 4;
const CONFETTI_DRAG = 1.8;
const STARS = 6;
const STAR_LIFE = 0.5;
/** Extra party colors mixed in with the rider's own. */
const PARTY = ["#ffe14d", "#ff4fb8", "#2ec4b6", "#ffffff"];

interface Piece {
  obj: THREE.Object3D;
  v: THREE.Vector3;
  spin: THREE.Vector3;
}

/** A small deterministic random stream, so every bail of a style plays the same. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A flat five-pointed star, for the puffs at the joints. */
function starGeometry(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 ? 0.05 : 0.12;
    const a = (i / 10) * Math.PI * 2 + Math.PI / 2;
    if (i === 0) shape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else shape.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  return new THREE.ShapeGeometry(shape);
}

/** Round soft points, each its own size (m) and opacity: the fireworks' sparks, flashes and glow. */
function sparkMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uHeight: { value: 800 } },
    vertexShader: /* glsl */ `
      attribute float size;
      attribute float alpha;
      attribute vec3 tint;
      uniform float uHeight;
      varying vec3 vTint;
      varying float vAlpha;
      void main() {
        vTint = tint;
        vAlpha = alpha;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mv;
        gl_PointSize = size * uHeight * 0.5 * projectionMatrix[1][1] / max(0.1, -mv.z);
      }`,
    fragmentShader: /* glsl */ `
      varying vec3 vTint;
      varying float vAlpha;
      void main() {
        float d = length(gl_PointCoord - 0.5) * 2.0;
        if (d > 1.0) discard;
        // A hot white core, the colour round it, a soft edge.
        vec3 col = mix(vTint, vec3(1.0), smoothstep(0.45, 0.0, d) * 0.6);
        gl_FragColor = vec4(col, vAlpha * (1.0 - smoothstep(0.55, 1.0, d)));
      }`,
    transparent: true,
    depthWrite: false,
  });
}

export class BailFx {
  /** Add to the scene once; it holds whatever the current bail throws about. */
  readonly root = new THREE.Group();
  /** The setting: a style, or Random for a fresh pick every bail (#128). */
  style: BailStyleId | Random = "classic";
  /** Low quality: the fireworks trimmed to about half (#199). */
  get lite(): boolean {
    return this.fireworks.lite;
  }
  set lite(on: boolean) {
    this.fireworks.lite = on;
  }
  /** The style the last bail played (or is playing). */
  played: BailStyleId = "classic";
  private playing: BailStyleId | null = null;
  /** Whether the skater was bailing last frame. */
  private down = false;
  private rider: BailRider | null = null;
  private lastT = 0;
  private t = 0;
  private pieces: Piece[] = [];
  private floor: (x: number, z: number) => number = () => 0;
  private readonly starGeo = starGeometry();
  private readonly starMat = new THREE.MeshBasicMaterial({ color: "#ffe14d", side: THREE.DoubleSide });
  private readonly stars: THREE.Mesh[] = [];
  private readonly confettiGeo = new THREE.PlaneGeometry(0.07, 0.045);
  private readonly confettiMat = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
  private readonly confetti = new THREE.InstancedMesh(this.confettiGeo, this.confettiMat, CONFETTI);
  private readonly bits = Array.from({ length: CONFETTI }, () => ({ p: new THREE.Vector3(), v: new THREE.Vector3(), r: new THREE.Euler(), spin: new THREE.Vector3() }));
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly one = new THREE.Vector3(1, 1, 1);
  private readonly fireworks = new Fireworks();
  private readonly color = new THREE.Color();
  private readonly spot = new THREE.Vector3();
  private readonly part = new THREE.Vector3();
  private readonly sparkGeo = new THREE.BufferGeometry();
  private readonly sparkMat = sparkMaterial();
  private readonly sparks = new THREE.Points(this.sparkGeo, this.sparkMat);
  private readonly bufferSize = new THREE.Vector2();
  /** The fireworks' sounds still to play, one a frame. */
  private readonly queued: Cue[] = [];

  constructor(private readonly picker = new CosmeticPicker(Math.random)) {
    this.root.name = "bail-fx";
    this.confetti.frustumCulled = false;
    this.confetti.visible = false;
    this.confetti.setColorAt(0, new THREE.Color()); // makes its color buffer now, not on the first bail
    this.root.add(this.confetti);
    const fw = this.fireworks;
    this.sparkGeo.setAttribute("position", new THREE.BufferAttribute(fw.positions, 3).setUsage(THREE.DynamicDrawUsage));
    this.sparkGeo.setAttribute("tint", new THREE.BufferAttribute(fw.colors, 3).setUsage(THREE.DynamicDrawUsage));
    this.sparkGeo.setAttribute("size", new THREE.BufferAttribute(fw.sizes, 1).setUsage(THREE.DynamicDrawUsage));
    this.sparkGeo.setAttribute("alpha", new THREE.BufferAttribute(fw.alphas, 1).setUsage(THREE.DynamicDrawUsage));
    this.sparkGeo.setDrawRange(0, 0);
    this.sparks.frustumCulled = false;
    this.sparks.visible = false;
    this.sparks.name = "fireworks";
    // Sized in metres: the shader needs the drawing buffer's height.
    this.sparks.onBeforeRender = (renderer) => {
      this.sparkMat.uniforms.uHeight!.value = renderer.getDrawingBufferSize(this.bufferSize).y;
    };
    this.root.add(this.sparks);
    for (let i = 0; i < STARS; i++) {
      const star = new THREE.Mesh(this.starGeo, this.starMat);
      star.visible = false;
      this.stars.push(star);
      this.root.add(star);
    }
  }

  /**
   * Plays the chosen style over a bail: starts it when the skater bails,
   * moves it along, and puts the rider back together when they're up.
   * `floor` is the ground height under a point. Returns a sound to play as
   * a bail starts, if the style has one.
   */
  update(sk: BailSkater, rider: BailRider, look: SkaterLook, dt: number, floor?: (x: number, z: number) => number): Cue | null {
    let cue: Cue | null = null;
    const bailing = sk.mode === "bail";
    // A new bail: the skater just went down, or the bail clock went back (one bail ended and another began between frames).
    const newBail = bailing && (!this.down || sk.bailT < this.lastT);
    this.down = bailing;
    if (newBail) this.played = this.picker.play("bailStyle", this.style);
    // Start it on a new bail, or again for a rider swapped mid-bail.
    const fresh = bailing && this.played !== "classic" && (newBail || this.playing === null || rider !== this.rider);
    if (this.playing !== null && (!bailing || fresh)) this.reset();
    this.lastT = bailing ? sk.bailT : 0;
    if (fresh) {
      if (floor) this.floor = floor;
      this.rider = rider;
      this.playing = this.played;
      this.t = 0;
      cue = this.played === "explode" ? this.explode(sk, rider) : this.played === "fireworks" ? this.launch(sk, rider, look) : this.burst(sk, look);
    }
    if (this.playing === null) return cue;
    this.t += dt;
    if (this.playing === "fireworks") {
      // The rider's gone up in the show (#210): the model shows itself every frame, so keep it hidden.
      rider.root.visible = false;
      this.queued.push(...this.fireworks.step(dt, this.floor));
      this.drawSparks();
      return cue ?? this.queued.shift() ?? null;
    }
    // The model shows the board (and nothing hides the rider) every frame, so keep them hidden while this plays.
    if (this.playing === "explode") {
      for (const part of rider.limbs) part.visible = false;
      this.stepPieces(dt);
    } else {
      rider.root.visible = false;
      this.stepConfetti(dt);
    }
    this.stepStars();
    return cue;
  }

  /** Ends whatever's playing and puts the rider back as it was. Call before a rider is swapped or freed. */
  reset(): void {
    const rider = this.rider;
    if (rider) {
      rider.root.visible = true;
      for (const part of rider.limbs) part.visible = true;
    }
    // The clones share the rider's geometry and materials: drop them, don't free them.
    for (const p of this.pieces) this.root.remove(p.obj);
    this.pieces = [];
    for (const s of this.stars) s.visible = false;
    this.confetti.visible = false;
    this.fireworks.clear();
    this.queued.length = 0;
    this.sparks.visible = false;
    this.sparkGeo.setDrawRange(0, 0);
    this.playing = null;
    this.rider = null;
  }

  /** Frees what this made (the star and confetti meshes). */
  dispose(): void {
    this.reset();
    this.starGeo.dispose();
    this.starMat.dispose();
    this.confettiGeo.dispose();
    this.confettiMat.dispose();
    this.confetti.dispose();
    this.sparkGeo.dispose();
    this.sparkMat.dispose();
  }

  /**
   * Fireworks (#199, #210): the rider becomes the show. A rocket leaves from
   * where the head, the torso (between the shoulders), the legs and the
   * board were, each in its own color, fanning out across the way they were
   * going. No sound yet: the first rocket's whistle comes with its launch.
   */
  private launch(sk: BailSkater, rider: BailRider, look: SkaterLook): null {
    const rgb = (c: THREE.ColorRepresentation) => {
      const col = this.color.set(c);
      return [col.r, col.g, col.b] as const;
    };
    rider.root.updateMatrixWorld(true);
    const [head, armF, armB, legL, legR, board] = rider.limbs;
    const at = (objs: (THREE.Object3D | undefined)[], drop: number, color: THREE.ColorRepresentation, i: number): FireworkOrigin => {
      const p = this.spot.set(0, 0, 0);
      let n = 0;
      for (const o of objs) {
        if (!o) continue;
        p.add(o.getWorldPosition(this.part));
        n++;
      }
      if (n) p.divideScalar(n);
      else p.set(sk.x, sk.y + 0.9 - i * 0.3, sk.z);
      // Never under the ground: the board lies on it.
      return { x: p.x, y: Math.max(p.y - drop, this.floor(p.x, p.z) + 0.1), z: p.z, color: rgb(color) };
    };
    const parts = [at([head], 0, look.hair.color, 0), at([armF, armB], 0, look.top.color, 1), at([legL, legR], 0.35, look.bottoms.color, 2), at([board], 0, look.deck, 3)];
    // Across the way they were going, so the bursts open side by side from behind.
    const speed = Math.hypot(sk.vx, sk.vz);
    const right: [number, number] = speed > 0.1 ? [-sk.vz / speed, sk.vx / speed] : [1, 0];
    this.fireworks.start(parts, [look.top.color, look.bottoms.color, look.hair.color, look.deck, look.wheels, look.shoes].map(rgb), right);
    rider.root.visible = false;
    this.sparks.visible = true;
    return null;
  }

  /** Hands the live sparks to the GPU. */
  private drawSparks(): void {
    const n = Math.min(MAX_SPARKS, this.fireworks.count);
    for (const name of ["position", "tint", "size", "alpha"]) {
      const attr = this.sparkGeo.getAttribute(name) as THREE.BufferAttribute;
      attr.clearUpdateRanges();
      attr.addUpdateRange(0, n * attr.itemSize);
      attr.needsUpdate = true;
    }
    this.sparkGeo.setDrawRange(0, n);
  }

  private explode(sk: BailSkater, rider: BailRider): Cue {
    const rand = rng(83);
    rider.root.updateMatrixWorld(true);
    const centre = new THREE.Vector3(sk.x, sk.y + 0.8, sk.z);
    rider.limbs.forEach((part, i) => {
      const obj = part.clone();
      part.matrixWorld.decompose(obj.position, obj.quaternion, obj.scale);
      obj.visible = true;
      this.root.add(obj);
      part.visible = false;
      // Out from the body and up, carrying some of the skater's slide.
      const out = obj.position.clone().sub(centre).setY(0);
      if (out.lengthSq() < 1e-4) out.set(Math.cos(i * 2.4), 0, Math.sin(i * 2.4));
      out.normalize().multiplyScalar(2 + rand() * 1.5);
      const v = new THREE.Vector3(out.x + sk.vx * 0.4, 3.5 + rand() * 2, out.z + sk.vz * 0.4);
      const spin = new THREE.Vector3(rand() - 0.5, rand() - 0.5, rand() - 0.5).multiplyScalar(18);
      this.pieces.push({ obj, v, spin });
      if (i < this.stars.length) this.placeStar(i, obj.position);
    });
    return "poof";
  }

  private burst(sk: BailSkater, look: SkaterLook): Cue {
    const rand = rng(38);
    const colors = [look.top.color, look.bottoms.color, look.hair.color, look.deck, look.wheels, look.shoes, ...PARTY].map((c) => new THREE.Color(c));
    this.bits.forEach((b, i) => {
      b.p.set(sk.x + (rand() - 0.5) * 0.4, sk.y + 0.3 + rand() * 1.3, sk.z + (rand() - 0.5) * 0.4);
      const a = rand() * Math.PI * 2;
      const out = 1.5 + rand() * 3.5;
      b.v.set(Math.cos(a) * out + sk.vx * 0.3, 2.5 + rand() * 4, Math.sin(a) * out + sk.vz * 0.3);
      b.r.set(rand() * 6, rand() * 6, rand() * 6);
      b.spin.set(rand() - 0.5, rand() - 0.5, rand() - 0.5).multiplyScalar(20);
      this.confetti.setColorAt(i, colors[i % colors.length]!);
    });
    if (this.confetti.instanceColor) this.confetti.instanceColor.needsUpdate = true;
    this.confetti.visible = true;
    this.stepConfetti(0);
    for (let i = 0; i < 3; i++) this.placeStar(i, new THREE.Vector3(sk.x + (i - 1) * 0.35, sk.y + 1 + (i % 2) * 0.3, sk.z));
    return "party";
  }

  private placeStar(i: number, at: THREE.Vector3): void {
    const star = this.stars[i]!;
    star.position.copy(at);
    star.rotation.set(0, i * 1.3, 0);
    star.visible = true;
  }

  /** Stars pop up at the joints, spin and shrink away. */
  private stepStars(): void {
    const k = Math.min(1, this.t / STAR_LIFE);
    for (const s of this.stars) {
      if (!s.visible) continue;
      const size = Math.sin(Math.PI * Math.min(1, k * 1.4)) * 1.3;
      s.scale.setScalar(Math.max(1e-3, size));
      s.rotation.y += 0.25;
      if (k >= 1) s.visible = false;
    }
  }

  private stepPieces(dt: number): void {
    for (const p of this.pieces) {
      p.v.y -= PIECE_G * dt;
      p.obj.position.addScaledVector(p.v, dt);
      p.obj.rotation.x += p.spin.x * dt;
      p.obj.rotation.y += p.spin.y * dt;
      p.obj.rotation.z += p.spin.z * dt;
      const ground = this.floor(p.obj.position.x, p.obj.position.z) + PIECE_REST;
      if (p.obj.position.y < ground) {
        p.obj.position.y = ground;
        p.v.y = Math.abs(p.v.y) * BOUNCE;
        p.v.x *= 0.7;
        p.v.z *= 0.7;
        p.spin.multiplyScalar(0.6);
      }
    }
  }

  private stepConfetti(dt: number): void {
    const drag = Math.exp(-CONFETTI_DRAG * dt);
    this.bits.forEach((b, i) => {
      const ground = this.floor(b.p.x, b.p.z) + 0.01;
      if (b.p.y > ground) {
        b.v.multiplyScalar(drag);
        b.v.y -= CONFETTI_G * dt;
        b.p.addScaledVector(b.v, dt);
        b.r.x += b.spin.x * dt;
        b.r.y += b.spin.y * dt;
        b.r.z += b.spin.z * dt;
      }
      if (b.p.y <= ground) {
        // Settled: lies flat on the ground.
        b.p.y = ground;
        b.v.set(0, 0, 0);
        b.r.set(-Math.PI / 2, 0, b.r.z);
      }
      this.q.setFromEuler(b.r);
      this.confetti.setMatrixAt(i, this.m.compose(b.p, this.q, this.one));
    });
    this.confetti.instanceMatrix.needsUpdate = true;
  }
}
